import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { AnswersJson, Field, FieldAnswer } from '@anuleaza/shared';
import { getChestionar, readExtractedPath } from '@anuleaza/shared';
import { api } from '../lib/api.js';
import { useRequireAuth } from '../lib/auth.js';
import { Button, Card, Logo } from '../components/primitives/index.js';
import { ChestionarSection, isSectionComplete } from '../components/composites/index.js';

export function ContestQuestions({ id }: { id: string }) {
  const me = useRequireAuth();
  const [, setLocation] = useLocation();

  const contest = useQuery({
    queryKey: ['contest', id],
    queryFn: () => api.contest(id),
    enabled: !!me.data,
  });

  const sections = useMemo(() => getChestionar(), []);
  const [sectionIdx, setSectionIdx] = useState(0);
  const [answers, setAnswers] = useState<Record<string, FieldAnswer>>({});

  // Build a per-field prefill map from the OCR-extracted JSON. Resolved once
  // when the contest data lands.
  const prefills = useMemo(() => {
    const extracted = contest.data?.contest.extracted_json ?? null;
    const result: Record<string, string | null> = {};
    for (const sec of sections) {
      for (const field of sec.fields) {
        if (field.prefillFrom) {
          result[field.id] = readExtractedPath(extracted, field.prefillFrom);
        }
      }
    }
    return result;
  }, [contest.data?.contest.extracted_json, sections]);

  // Auto-commit prefills into the answers state ONCE on contest-data arrival.
  // Without this, a prefilled text field shows the value visually but the
  // state thinks it's still empty → "Continuă" stays disabled even though the
  // user sees a populated field. The user-can-still-edit semantics are preserved
  // because the input's onChange writes new values into the same state slot.
  const prefillSeeded = useRef(false);
  useEffect(() => {
    if (prefillSeeded.current) return;
    if (!contest.data?.contest.extracted_json) return;
    const seed: Record<string, FieldAnswer> = {};
    for (const [fieldId, value] of Object.entries(prefills)) {
      if (value && value.trim()) {
        seed[fieldId] = { type: 'text', value };
      }
    }
    if (Object.keys(seed).length > 0) {
      setAnswers((prev) => ({ ...seed, ...prev }));
    }
    prefillSeeded.current = true;
  }, [contest.data?.contest.extracted_json, prefills]);

  const save = useMutation({
    mutationFn: () => {
      // "note_libere" is the last-section textarea — surface as top-level notes
      // for the LLM, separate from the structured answers.
      const notesAnswer = answers.note_libere;
      const notes =
        notesAnswer?.type === 'text' ? notesAnswer.value.trim() || undefined : undefined;
      const fieldsCopy: Record<string, FieldAnswer> = { ...answers };
      delete fieldsCopy.note_libere;
      const payload: AnswersJson = { fields: fieldsCopy, notes };
      return api.saveAnswers(id, payload);
    },
    onSuccess: () => setLocation(`/contest/${id}/analyzing`),
  });

  if (me.isLoading || contest.isLoading) {
    return (
      <Shell>
        <div className="text-muted text-sm py-12 text-center">Se încarcă…</div>
      </Shell>
    );
  }

  const c = contest.data?.contest;
  if (!c || !c.extracted_json) {
    return (
      <Shell>
        <Card className="max-w-md mx-auto text-center">
          <h1 className="text-xl font-medium mb-2">Date indisponibile</h1>
          <p className="text-muted text-sm mb-6">
            Procesarea PV-ului nu s-a încheiat încă. Reîncearcă în câteva secunde.
          </p>
          <Button variant="ghost" pill icon={false} onClick={() => setLocation('/dashboard')}>
            ← La dashboard
          </Button>
        </Card>
      </Shell>
    );
  }

  const currentSection = sections[sectionIdx];
  if (!currentSection) return null;
  const isLast = sectionIdx === sections.length - 1;
  const canContinue = isSectionComplete(currentSection, answers);
  const missing = canContinue ? [] : listMissingRequired(currentSection.fields, answers);

  const handleFieldChange = (fieldId: string, answer: FieldAnswer | null) => {
    setAnswers((prev) => {
      const next = { ...prev };
      if (answer === null) delete next[fieldId];
      else next[fieldId] = answer;
      return next;
    });
  };

  const onNext = () => {
    if (!isLast) {
      setSectionIdx((i) => i + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      save.mutate();
    }
  };

  const onBack = () => {
    if (sectionIdx > 0) {
      setSectionIdx((i) => i - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      setLocation(`/contest/${id}/confirm`);
    }
  };

  return (
    <Shell>
      <div className="max-w-2xl mx-auto">
        <ChestionarSection
          section={currentSection}
          sectionIndex={sectionIdx}
          totalSections={sections.length}
          answers={answers}
          prefills={prefills}
          onChange={handleFieldChange}
        />

        {save.isError && (
          <div className="mt-6 text-sm text-danger">
            A apărut o problemă la salvare. Reîncearcă.
          </div>
        )}

        {missing.length > 0 && (
          <div className="mt-6 px-4 py-3 rounded-lg bg-warn-soft border border-warn/40 text-sm text-warn-text">
            <div className="font-medium mb-1">Câmpuri obligatorii nesoluționate:</div>
            <ul className="list-disc list-inside leading-relaxed">
              {missing.map((label) => (
                <li key={label}>{label}</li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-8 flex justify-between gap-3">
          <Button variant="ghost" pill icon={false} onClick={onBack}>
            ← {sectionIdx === 0 ? 'Înapoi la date PV' : 'Secțiunea anterioară'}
          </Button>
          <Button
            pill
            disabled={!canContinue || save.isPending}
            onClick={onNext}
            icon={save.isPending ? null : '→'}
          >
            {save.isPending
              ? 'Salvăm…'
              : isLast
                ? 'Trimite spre analiză'
                : 'Continuă'}
          </Button>
        </div>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-dvh bg-bg">
      <nav className="px-4 md:px-8 py-4 md:py-5 border-b border-line">
        <Logo />
      </nav>
      <main className="px-4 md:px-6 py-6 md:py-10">{children}</main>
    </div>
  );
}

/** Returns human-readable question text for every visible required field that's
 *  still missing — used to tell the user exactly what blocks "Continuă". */
function listMissingRequired(
  fields: Field[],
  answers: Record<string, FieldAnswer>,
): string[] {
  const missing: string[] = [];
  for (const f of fields) {
    if (!f.required) continue;
    // Honor showWhen — hidden fields are never blockers.
    if (f.showWhen) {
      const dep = answers[f.showWhen.fieldId];
      if (!dep) continue;
      if (dep.type === 'radio' && dep.optionId !== f.showWhen.equals) continue;
      if (dep.type === 'multi' && !dep.optionIds.includes(f.showWhen.equals)) continue;
    }
    const a = answers[f.id];
    let answered = false;
    if (a) {
      if (a.type === 'radio') answered = !!a.optionId;
      else if (a.type === 'multi') answered = a.optionIds.length > 0;
      else if (a.type === 'text') answered = !!a.value.trim();
      else if (a.type === 'bool') answered = true;
    }
    if (!answered) missing.push(f.question);
  }
  return missing;
}
